/**
 * Website activity — what this contact browsed on your sites, grouped by
 * visit. Renders nothing at all when the contact has no linked browsing:
 * most contacts arrive by import or manual entry, and an empty "no activity"
 * card on every one of them would just be noise.
 *
 * Data appears here once the visitor is identified (the identify endpoint
 * links their anonymous browsing to this contact and back-dates the link),
 * so the panel often answers "what did they read BEFORE they got in touch?".
 */

import { useEffect, useState } from "react";
import { Globe } from "lucide-react";
import { api } from "@/lib/api";
import { channelColor, timeAgo } from "@/features/traffic/traffic-shared";

interface ActivityEvent {
  ts: string;
  visit: string | null;
  project: string;
  path: string;
  source: string;
  channel: string;
  device: string | null;
  country: string | null;
}
interface ActivitySummary {
  views: number;
  visits: number;
  firstSeen: string | null;
  lastSeen: string | null;
}

interface VisitGroup {
  key: string;
  when: string;
  channel: string;
  source: string;
  device: string | null;
  pages: { ts: string; path: string }[];
}

function groupByVisit(events: ActivityEvent[]): VisitGroup[] {
  const groups: VisitGroup[] = [];
  const byKey = new Map<string, VisitGroup>();
  for (const e of events) {
    const key = e.visit ?? e.ts;
    let g = byKey.get(key);
    if (!g) {
      g = {
        key,
        when: e.ts,
        channel: e.channel,
        source: e.source,
        device: e.device,
        pages: [],
      };
      byKey.set(key, g);
      groups.push(g);
    }
    g.pages.push({ ts: e.ts, path: e.path });
  }
  // Events arrive newest-first; within a visit show pages in the order they
  // were actually opened.
  for (const g of groups) g.pages.reverse();
  return groups;
}

export function WebsiteActivityPanel({ contactId }: { contactId: string }) {
  const [events, setEvents] = useState<ActivityEvent[] | null>(null);
  const [summary, setSummary] = useState<ActivitySummary | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ events: ActivityEvent[]; summary: ActivitySummary }>(
        `/site-analytics/contact/${contactId}`,
      )
      .then((res) => {
        if (cancelled) return;
        setEvents(res.events);
        setSummary(res.summary);
      })
      .catch(() => {
        // Silent: a contact page must never toast because analytics is empty
        // or the account has no tracked sites.
        if (!cancelled) setEvents([]);
      });
    return () => {
      cancelled = true;
    };
  }, [contactId]);

  if (!events || events.length === 0 || !summary) return null;

  const groups = groupByVisit(events);
  const shown = expanded ? groups : groups.slice(0, 3);

  return (
    <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-4">
      <h3 className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
        <Globe className="h-3 w-3" /> Website activity
      </h3>
      <p className="mb-3 text-xs text-zinc-500">
        {summary.views.toLocaleString()} page{summary.views === 1 ? "" : "s"} over{" "}
        {summary.visits.toLocaleString()} visit{summary.visits === 1 ? "" : "s"}
        {summary.lastSeen && <> · last {timeAgo(summary.lastSeen)}</>}
      </p>
      {/* Bounded height: a long history scrolls INSIDE the card instead of
          stretching the whole rail (the API already caps at 200 events). */}
      <div className="max-h-[26rem] space-y-3 overflow-y-auto pr-1">
        {shown.map((g) => (
          <div key={g.key}>
            <div className="flex items-center gap-2 text-xs text-zinc-400">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: channelColor(g.channel) }}
                aria-hidden
              />
              <span className="font-medium text-zinc-300">
                {new Date(g.when).toLocaleDateString(undefined, {
                  day: "numeric",
                  month: "short",
                })}
              </span>
              <span className="truncate text-zinc-500">
                {g.channel === "Direct" ? "came directly" : `via ${g.source || g.channel}`}
                {g.device ? ` · ${g.device.toLowerCase()}` : ""}
              </span>
            </div>
            <ul className="mt-1 space-y-0.5 border-l border-zinc-800 pl-3.5">
              {g.pages.slice(0, 6).map((p, i) => (
                <li key={`${p.ts}-${i}`} className="flex items-center justify-between gap-2 text-xs">
                  <span className="truncate font-mono text-zinc-400">{p.path}</span>
                  <span className="shrink-0 tabular-nums text-zinc-600">
                    {new Date(p.ts).toLocaleTimeString(undefined, {
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </span>
                </li>
              ))}
              {g.pages.length > 6 && (
                <li className="text-xs text-zinc-600">…and {g.pages.length - 6} more pages</li>
              )}
            </ul>
          </div>
        ))}
      </div>
      {groups.length > 3 && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 cursor-pointer text-xs text-rose-400 hover:text-rose-300"
        >
          {expanded ? "Show fewer visits" : `Show all ${groups.length} visits`}
        </button>
      )}
    </div>
  );
}
